import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Avatar,
  Tooltip,
  IconButton,
  CircularProgress,
  Container,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import UploadIcon from "@mui/icons-material/Upload";
import MonitorIcon from "@mui/icons-material/Monitor";
import AlertIcon from "@mui/icons-material/NotificationImportant";
import ReportIcon from "@mui/icons-material/Assessment";
import BotIcon from "@mui/icons-material/SmartToy";
import TransactionIcon from "@mui/icons-material/SwapHoriz";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import "./Home.css";
import { decrypt } from "../../Encrypdecryp/Crypt";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Legend,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Legend,
  ChartTooltip
);

const Home = () => {
  const navigate = useNavigate();
  const isAdmin = window.localStorage
    ? localStorage.getItem("userRole") === "admin"
    : false;

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animatedStats, setAnimatedStats] = useState({
    users: 0,
    customers: 0,
    transactions: 0,
    alerts: 0,
  });

  

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No authentication token found.");

        const response = await axios.get(`${API_BASE_URL}/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = response.data;
        const decryptedData = {
          stats: {
            ...data.stats,
            active_users: data.stats.active_users,
            customers_profiled: {
              total: data.stats.customers_profiled.total,
              growth_percent: data.stats.customers_profiled.growth_percent,
            },
            transactions: {
              total: data.stats.transactions.total,
              growth_percent: data.stats.transactions.growth_percent,
            },
            alerts_managed: {
              total: data.stats.alerts_managed.total,
              growth_percent: data.stats.alerts_managed.growth_percent,
            },
          },
          recent: {
            customer_profiles: data.recent.customer_profiles.map((customer) => ({
              ...customer,
              finalized_data: {
                ...customer.finalized_data,
                fullName:
                  decrypt(customer.finalized_data.fullName) ||
                  customer.finalized_data.fullName,
              },
            })),
            transactions: data.recent.transactions.map((tx) => ({
              ...tx,
              name: decrypt(tx.name) || tx.name,
            })),
          },
          charts: data.charts,
        };

        setDashboardData(decryptedData);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [API_BASE_URL]);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimatedStats((prev) => ({
        users: dashboardData
          ? Math.min(prev.users + 5, dashboardData.stats.active_users || 0)
          : 0,
        customers: dashboardData
          ? Math.min(
              prev.customers + 3,
              dashboardData.stats.customers_profiled.total || 0
            )
          : 0,
        transactions: dashboardData
          ? Math.min(
              prev.transactions + 10,
              dashboardData.stats.transactions.total || 0
            )
          : 0,
        alerts: dashboardData
          ? Math.min(
              prev.alerts + 1,
              dashboardData.stats.alerts_managed.total || 0
            )
          : 0,
      }));
    }, 50);

    return () => clearInterval(interval);
  }, [dashboardData]);

  const features = [
    {
      title: "Customer Profiling",
      description: "Advanced KYC and risk assessment for clients.",
      icon: <PeopleIcon fontSize="large" />,
      path: "/customer-profiling",
    },
    {
      title: "Initiate Trade",
      description: "Secure upload and analysis of trade documents.",
      icon: <UploadIcon fontSize="large" />,
      path: "/view-and-upload",
    },
    {
      title: "Transaction Chains",
      description: "Visualize and track complex trade flows.",
      icon: <TransactionIcon fontSize="large" />,
      path: "/transaction-chain",
    },
    {
      title: "Real-Time Monitoring",
      description: "AI-powered alerts and compliance checks.",
      icon: <MonitorIcon fontSize="large" />,
      path: "/monitoring",
    },
    {
      title: "Advanced Reporting",
      description: "Generate comprehensive compliance reports.",
      icon: <ReportIcon fontSize="large" />,
      path: "/reporting",
    },
    {
      title: "Bot Configuration",
      description: "Customize AI bots for automated tasks.",
      icon: <BotIcon fontSize="large" />,
      path: "/configure-bot",
    },
    {
      title: "Integrated Tools",
      description: "Connect with external APIs and services.",
      icon: <SettingsIcon fontSize="large" />,
      path: "/integrated-tools",
    },
    {
      title: "Admin Panel",
      description: isAdmin
        ? "Full system administration."
        : "Admin access required.",
      icon: <AdminPanelSettingsIcon fontSize="large" />,
      path: isAdmin ? "/admin-panel" : null,
      disabled: !isAdmin,
    },
  ];

  // ✅ Use real API chart data
  const alertsByBots = dashboardData?.charts?.alerts_by_bots || [];
  const alertsOverTime = dashboardData?.charts?.alerts_over_time || [];

  const barData = {
    labels: alertsByBots.map((a) => a.bot),
    datasets: [
      {
        label: "Alerts",
        data: alertsByBots.map((a) => a.count),
        backgroundColor: ["#10b981", "#f59e0b", "#ef4444", "#3b82f6"],
        borderColor: ["#059669", "#d97706", "#dc2626", "#2563eb"],
        borderWidth: 1,
      },
    ],
  };

  const lineData = {
    labels: alertsOverTime.map((a) => a.date),
    datasets: [
      {
        label: "Alerts Over Time",
        data: alertsOverTime.map((a) => a.count),
        borderColor: "#667eea",
        backgroundColor: "rgba(102, 126, 234, 0.1)",
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#667eea",
        pointBorderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1 } },
    },
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="home-container">
      <div className="particles">
        {[...Array(30)].map((_, i) => (
          <div key={i} className="particle" style={{ "--i": i }} />
        ))}
      </div>

      <Container maxWidth="lg">
        {/* Stats Section */}
        <Box className="stats-section">
          <Grid container spacing={3} justifyContent="center">
            {[
              {
                label: "Active Users",
                value: animatedStats.users,
                icon: <PeopleIcon />,
                growth: dashboardData?.stats.customers_profiled.growth_percent,
                gradient: "var(--gradient-primary)",
              },
              {
                label: "Customers Profiled",
                value: animatedStats.customers,
                icon: <PeopleIcon />,
                growth: dashboardData?.stats.customers_profiled.growth_percent,
                gradient: "var(--gradient-customers)",
                class: "customers",
              },
              {
                label: "Transactions",
                value: animatedStats.transactions,
                icon: <TransactionIcon />,
                growth: dashboardData?.stats.transactions.growth_percent,
                gradient: "var(--gradient-transactions)",
                class: "transactions",
              },
              {
                label: "Alerts",
                value: animatedStats.alerts,
                icon: <AlertIcon />,
                growth: dashboardData?.stats.alerts_managed.growth_percent,
                gradient: "var(--gradient-alerts)",
                class: "alerts",
              },
            ].map((stat, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box
                  className={`card stat-card ${stat.class || ""}`}
                  sx={{
                    background: stat.gradient.includes("linear-gradient")
                      ? stat.gradient
                      : "var(--card-background)",
                    backdropFilter: "var(--backdrop-blur)",
                    border: "1px solid var(--border-color)",
                    boxShadow: "var(--box-shadow)",
                    transition: "var(--transition-cubic)",
                    "&:hover": {
                      borderColor: "var(--border-hover-color)",
                      boxShadow: "var(--box-shadow-hover)",
                      transform: "translateY(-8px)",
                    },
                  }}
                >
                  <div className="stat-card-inner">
                    <div
                      className={`icon-wrapper ${stat.class || ""}`}
                      style={{ background: stat.gradient }}
                    >
                      {stat.icon}
                    </div>
                    <div className="stat-content">
                      <Typography variant="h3" className="stat-number">
                        {stat.value}
                      </Typography>
                      <Typography variant="body1" className="stat-label">
                        {stat.label}
                      </Typography>
                    </div>
                    <div className="stat-trend">
                      <TrendingUpIcon fontSize="small" />
                      <span>{stat.growth || 0}%</span>
                    </div>
                  </div>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Insights Section */}
        <Box className="insights-section">
          <Typography variant="h3" className="section-title">
            Trade Compliance Insights
          </Typography>
          <div className="insights-grid">
            <Paper className="card insight-card" elevation={0}>
              <div className="insight-header">
                <Typography variant="h6" className="insight-title">
                  Recent Customer Profiles
                </Typography>
              </div>
              <List className="insight-list">
                {dashboardData?.recent.customer_profiles.map((customer) => (
                  <ListItem key={customer.id} className="insight-list-item">
                    <ListItemIcon>
                      <Avatar className="customer-avatar">
                        {customer.finalized_data.fullName.charAt(0)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={customer.finalized_data.fullName}
                      secondary={`Last Activity: ${
                        customer.created_at || "N/A"
                      }`}
                      className="insight-text"
                    />
                   
                  </ListItem>
                ))}
              </List>
              <Button
                className="btn-outlined view-all-button"
                onClick={() => navigate("/customer-profiling")}
              >
                View All Profiles
              </Button>
            </Paper>

            <Paper className="card insight-card" elevation={0}>
              <div className="insight-header">
                <Typography variant="h6" className="insight-title">
                  Recent Transactions
                </Typography>
              </div>
              <List className="insight-list">
                {dashboardData?.recent.transactions.map((tx) => (
                  <ListItem key={tx.id} className="insight-list-item">
                    <ListItemIcon>
                      <div className="transaction-icon icon-wrapper">
                        <TransactionIcon />
                      </div>
                    </ListItemIcon>
                    <ListItemText
                      primary={tx.name}
                      secondary={`${tx.reference_id} • ${
                        tx.created_date || "N/A"
                      }`}
                      className="insight-text"
                    />
                  </ListItem>
                ))}
              </List>
              <Button
                className="btn-outlined view-all-button"
                onClick={() => navigate("/transaction-chain")}
              >
                View All Chains
              </Button>
            </Paper>

            <Paper className="card insight-card" elevation={0}>
              <Typography variant="h6" className="insight-title">
                Alerts By Bots
              </Typography>
              <Box className="chart-container">
                <Bar data={barData} options={chartOptions} />
              </Box>
              <Button
                className="btn-outlined view-all-button"
                onClick={() => navigate("/monitoring")}
              >
                View All Alerts
              </Button>
            </Paper>

            <Paper className="card insight-card" elevation={0}>
              <Typography variant="h6" className="insight-title">
                Alerts Over Time
              </Typography>
              <Box className="chart-container">
                <Line data={lineData} options={chartOptions} />
              </Box>
              <Button
                className="btn-outlined view-all-button"
                onClick={() => navigate("/monitoring")}
              >
                View Trends
              </Button>
            </Paper>
          </div>
        </Box>

        {/* Features Section */}
        <Box className="features-section">
          <Typography variant="h3" className="section-title">
            Comprehensive Platform Capabilities
          </Typography>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card className="card feature-card" elevation={0}>
                  <CardContent className="feature-content">
                    <div className="feature-icon-wrapper icon-wrapper">
                      {feature.icon}
                    </div>
                    <Typography variant="h6" className="feature-title">
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      className="feature-description"
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                  <CardActions className="feature-actions">
                    <Button
                      className="btn-contained feature-button"
                      disabled={feature.disabled}
                      onClick={() => feature.path && navigate(feature.path)}
                      fullWidth
                      sx={{
                        color: feature.disabled
                          ? "var(--text-muted)"
                          : "white",
                        "&.Mui-disabled": {
                          background: "rgba(107, 114, 128, 0.3)",
                          color: "rgba(255, 255, 255, 0.4)",
                        },
                      }}
                    >
                      {feature.disabled ? "Restricted" : "Access"}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Home;